// src/components/lead-tracker/LeadTrackerYear.jsx
// ANNUAL METRICS equivalent: 12 month columns + totals, plus Top Referrers
// (people) and Top Channels (sources) with converted counts.
import React, { useMemo } from "react";
import {
  yearMetrics,
  topReferrers,
  topChannels,
  MONTH_LABELS,
} from "../../utils/leadTracker";

const pct = (v) => (v == null ? "-" : `${Math.round(v * 100)}%`);

export default function LeadTrackerYear({ rows, year, goals }) {
  const y = useMemo(() => yearMetrics(rows, year, goals), [rows, year, goals]);
  const referrers = useMemo(() => topReferrers(rows, year), [rows, year]);
  const channels = useMemo(() => topChannels(rows, year), [rows, year]);

  const metricRows = [
    { label: "Leads", get: (m) => m.leads, total: y.totals.leads, goal: y.totals.leadsGoal },
    { label: "Appts", get: (m) => m.appts, total: y.totals.appts },
    { label: "Lead to Appt %", get: (m) => pct(m.leadToApptPct) },
    { label: "Contracts", get: (m) => m.contracts, total: y.totals.contracts },
    { label: "Appt to Contract %", get: (m) => pct(m.apptToContractPct) },
    { label: "Closings", get: (m) => m.closings, total: y.totals.closings, goal: y.totals.closingsGoal },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              <th className="px-2.5 py-2 text-left font-semibold">Metric</th>
              {MONTH_LABELS.map((m) => (
                <th key={m} className="px-2 py-2 font-semibold">{m}</th>
              ))}
              <th className="px-2.5 py-2 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {metricRows.map((mr) => (
              <tr key={mr.label} className="border-t border-slate-100">
                <td className="px-2.5 py-1.5 font-semibold text-slate-700">{mr.label}</td>
                {y.months.map((m, i) => (
                  <td key={i} className="px-2 py-1.5 text-center">{mr.get(m)}</td>
                ))}
                <td className="px-2.5 py-1.5 text-center font-bold">
                  {mr.total != null ? mr.total : "-"}
                  {mr.goal != null ? ` / ${mr.goal}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-sm font-bold text-slate-800 mb-2">Top Referrers</div>
          {referrers.length === 0 && (
            <div className="text-xs text-slate-400">
              No referrer links yet. Set them on leads or contacts.
            </div>
          )}
          {referrers.slice(0, 10).map((r) => (
            <div key={r.contactId} className="flex justify-between text-xs py-1 border-t border-slate-50">
              <span className="font-semibold text-slate-700">{r.name || "Unnamed"}</span>
              <span className="text-slate-500">
                {r.total} lead{r.total === 1 ? "" : "s"} · {r.converted} converted
              </span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-sm font-bold text-slate-800 mb-2">Top Channels</div>
          {channels.length === 0 && (
            <div className="text-xs text-slate-400">No sourced leads this year.</div>
          )}
          {channels.slice(0, 10).map((c) => (
            <div key={c.sourceValue} className="flex justify-between text-xs py-1 border-t border-slate-50">
              <span className="font-semibold text-slate-700">
                {c.name || c.sourceValue} <span className="text-slate-400">({c.bucket})</span>
              </span>
              <span className="text-slate-500">
                {c.total} lead{c.total === 1 ? "" : "s"} · {c.converted} converted
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
